import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { Base, MagnifyingGlassIcon } from '@itsrever/design-system'
import { stripBarcodeScannerPrefix } from '@/utils'
import { DateRangePicker } from '@/ui/shared/date-range'
import { type ChangeEvent, useState } from 'react'
import { FiltersPanel } from './filters-panel'
import { FilterPills } from './filter-pills'
import { FILTER_KEYS } from './helpers/use-process-filters'

// What "Clear all" clears: the search, the dates and every filter of the panel.
const FILTER_PARAMS = ['search', 'from', 'to', ...FILTER_KEYS]

const { Input } = Base

export const ProcessesFilters = () => {
  const { t } = useTranslation()

  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const search = searchParams.get('search') ?? ''
  const from = searchParams.get('from')
  const to = searchParams.get('to')

  const [searchInputText, setSearchInputText] = useState(
    stripBarcodeScannerPrefix(search)
  )

  const resetPage = () => searchParams.delete('page')

  const resetFilters = () => {
    setSearchInputText('')
    navigate(location.pathname, { replace: true })
  }

  const displayClearFiltersButton = FILTER_PARAMS.some((param) =>
    searchParams.has(param)
  )

  return (
    <div className="border-y border-neutral-200 pb-3 pt-4">
      <div className="flex gap-2">
        <div className="w-[19rem]">
          <Input
            value={searchInputText}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const value = stripBarcodeScannerPrefix(e.target.value)
              setSearchInputText(value)

              if (value.length > 0) {
                resetPage()
                searchParams.set('search', value)
              } else {
                searchParams.delete('search')
              }
              setSearchParams(searchParams)
            }}
            type="search"
            placeholder={t('orders.search_by')}
            prefix={<MagnifyingGlassIcon className="size-5" />}
            clear={searchInputText ? searchInputText.length > 0 : false}
            onClear={() => {
              setSearchInputText('')
              searchParams.delete('search')
              setSearchParams(searchParams)
            }}
          />
        </div>
        <div className="w-60">
          <DateRangePicker
            selected={{
              from: from ? new Date(from) : undefined,
              to: to ? new Date(to) : undefined
            }}
            onSelect={(date) => {
              resetPage()
              searchParams.set('from', date?.from?.toISOString() ?? '')
              searchParams.set('to', date?.to?.toISOString() ?? '')
              setSearchParams(searchParams)
            }}
          />
        </div>
        <FiltersPanel />
        {displayClearFiltersButton && (
          <button
            className="ml-2 cursor-pointer text-nowrap text-xs font-medium text-neutral-600 underline-offset-4 transition-all hover:underline"
            onClick={resetFilters}
          >
            {t('orders.clear_all')}
          </button>
        )}
      </div>
      <FilterPills />
    </div>
  )
}
